import { FC, MouseEvent, useCallback } from 'react';
import { openExternalLink } from '../../../../utils/openExternalLink';
import { BusinessType } from '../../../business/types';
import { IconBookmark } from '../../../icon/IconBookmark';
import { IconBookmarkFilled } from '../../../icon/IconBookmarkFilled';
import { IconDislike } from '../../../icon/IconDislike';
import { IconDislikeFilled } from '../../../icon/IconDislikeFilled';
import { IconLike } from '../../../icon/IconLike';
import { IconShare } from '../../../icon/IconShare';
import { IconWebsite } from '../../../icon/IconWebsite';
import { IconButton } from '../../../input/IconButton';
import { DataTestId } from '../../../layout/DataTestIdWrapper';
import { PostUI, ReactionState } from '../../types';
import { PostDetailsCardProps, usePostDetailsCardContext } from '../PostDetailsCard';
import { PostTextControlsContainer } from '../styles';
import { makeMediaParams } from '../utils';
import { isPostStatusValidForSave } from './utils/isPostStatusValidForSave';

export interface PostTextControlsProps {
    post: PostUI;
    index: PostDetailsCardProps['index'];
    onReaction: PostDetailsCardProps['onReaction'];
    onBookmark?: PostDetailsCardProps['onBookmark'];
    onShare?: PostDetailsCardProps['onShare'];
}

export const PostTextControls: FC<PostTextControlsProps> = ({
    post,
    index,
    onReaction,
    onBookmark,
    onShare,
}) => {
    const { activeIndex } = usePostDetailsCardContext();

    const handleBookmark = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            onBookmark?.();
            event.stopPropagation();
        },
        [onBookmark],
    );

    const handleReaction = useCallback(
        (reactionState: PostUI['reactionState']) => {
            onReaction({ postNumber: index, reactionState });
        },
        [index, onReaction],
    );

    const handleShare = useCallback(
        () => onShare?.(makeMediaParams(post, activeIndex)),
        [onShare, post, activeIndex],
    );

    const handleWebsiteClick = useCallback(() => {
        if (post.business?.siteUrl) {
            openExternalLink(post.business.siteUrl);
        }
    }, [post.business?.siteUrl]);

    return (
        <PostTextControlsContainer>
            <IconButton
                dataTestId={DataTestId.LIKE}
                onClick={(e) => {
                    e.stopPropagation();
                    handleReaction(ReactionState.LIKE);
                }}
            >
                <IconLike
                    fontSize={24}
                    strokeWidth="2px"
                    stroke="var(--color-primary)"
                    fill={post.reactionState === 'like' ? 'var(--color-primary)' : 'transparent'}
                    dataTestId={
                        post.reactionState === 'like' ? DataTestId.FILLED : DataTestId.EMPTY
                    }
                />
            </IconButton>

            {post.business ? (
                <IconButton
                    dataTestId={DataTestId.DISLIKE}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleReaction(ReactionState.DISLIKE);
                    }}
                >
                    {post.reactionState === 'dislike' ? (
                        <IconDislikeFilled
                            fontSize={24}
                            strokeWidth="2px"
                            fill="var(--color-primary)"
                            dataTestId={DataTestId.FILLED}
                        />
                    ) : (
                        <IconDislike
                            fontSize={24}
                            strokeWidth="2px"
                            fill="var(--color-primary)"
                            dataTestId={DataTestId.EMPTY}
                        />
                    )}
                </IconButton>
            ) : null}

            {isPostStatusValidForSave(post.status) && !post.ownersData?.isOwner && (
                <IconButton onClick={handleBookmark} dataTestId={DataTestId.BOOKMARK}>
                    {post.saved ? (
                        <IconBookmarkFilled fontSize={24} color="var(--color-primary)" />
                    ) : (
                        <IconBookmark fontSize={24} color="var(--color-primary)" />
                    )}
                </IconButton>
            )}

            <IconButton onClick={handleShare} dataTestId={DataTestId.SHARE}>
                <IconShare color="var(--color-primary)" />
            </IconButton>

            {post.business?.type === BusinessType.BUSINESS && Boolean(post.business?.siteUrl) && (
                <IconButton onClick={handleWebsiteClick}>
                    <IconWebsite fontSize={24} color="var(--color-primary)" />
                </IconButton>
            )}
        </PostTextControlsContainer>
    );
};
